// Mirrors the mobile app's src/Utils/analytics/scrubEventProperties.ts scrubbing policy so web
// telemetry (Sentry + PostHog) never captures the `?token=` credential used by the Market Shop /
// Visitor Parking / Package Room mini-app flows (see getTokenFromURL). Recurses to depth 4 on
// both SDKs — the mobile PostHog scrubber is shallow by design, this one intentionally isn't.
const SENSITIVE_KEYS = ['token', 'authorization', 'auth']

// Routes (see auth.routes.tsx / valet-parking.routes.tsx) that pass a credential as a path
// segment rather than a query param — e.g. `#/change-password/user@example.com/abc123`, or
// `#/parking/:ticketNumber`, which (see getParkingTicket/requestVehicle/payForParking in
// api.js) is looked up with no auth token at all, making the ticket number itself a bearer
// credential same as the `?token=` mini-app flows. scrubString's query-param regex above can't
// reach these, so they get their own pass. Param names/order must mirror the route definitions,
// and only as many path segments as there are params get consumed, so e.g.
// `/parking/12345/payment` scrubs to `/parking/:ticketNumber/payment` — recognizable as the
// route, with any trailing (non-sensitive) sub-path preserved, rather than an opaque redaction.
//
// This is a second definition of those routes' shape, so it can drift from the real one in
// auth.routes.tsx/valet-parking.routes.tsx if either changes without the other — exported only
// so scrub-event-properties.route-sync.test.ts can catch that drift; nothing else should import
// this.
export const SENSITIVE_PATH_PARAMS: Record<string, string[]> = {
  activate: ['email', 'hash'],
  'change-password': ['email', 'hash'],
  parking: ['ticketNumber'],
  'setup-password': ['email'],
}

const MAX_SCRUB_DEPTH = 4

const REDACTED = '[Filtered]'

const isSensitiveKey = (key: string) => SENSITIVE_KEYS.includes(key.toLowerCase())

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Redacts sensitive query-string / URL-fragment params (`?token=`, `&auth=`, ...) and the
 * email/hash path segments of the auth routes above, wherever they appear in a string, whether
 * that's a real URL or a hash-based route like `#/market-shop?token=XXX&type=resident`.
 */
const QUERY_PARAM_SCRUBBERS = SENSITIVE_KEYS.map((key) => new RegExp(`([?&]${key}=)[^&#]*`, 'gi'))

// A path segment ends at the next URL delimiter *and* at anything that can't be part of a URL
// (quotes, whitespace, `<>`) — these URLs are often embedded in larger strings (`$elements_chain`
// `href="#/parking/X"nth-child=...`, error messages, JSON request bodies), and consuming past the
// URL's end would silently delete the rest of that string.
const PATH_SEGMENT = `/[^/?#&\\s"'<>]+`

const PATH_PARAM_SCRUBBERS = Object.entries(SENSITIVE_PATH_PARAMS).map(([segment, params]) => ({
  pattern: new RegExp(`(/${segment})(${PATH_SEGMENT.repeat(params.length)})`, 'gi'),
  placeholder: `$1/${params.map((param) => `:${param}`).join('/')}`,
}))

export const scrubString = (value: string): string => {
  const withScrubbedQueryParams = QUERY_PARAM_SCRUBBERS.reduce(
    (acc, pattern) => acc.replace(pattern, `$1${REDACTED}`),
    value,
  )

  return PATH_PARAM_SCRUBBERS.reduce(
    (acc, { pattern, placeholder }) => acc.replace(pattern, placeholder),
    withScrubbedQueryParams,
  )
}

export const scrubEventProperties = <T>(value: T, depth = MAX_SCRUB_DEPTH): T => {
  // Strings are leaves - always scrub them regardless of remaining depth. Only object/array
  // traversal is depth-limited, so a token buried in `event.request.url` a few levels down isn't
  // missed just because the recursion budget was spent getting there.
  if (typeof value === 'string') {
    return scrubString(value) as unknown as T
  }

  if (depth <= 0) return value

  if (Array.isArray(value)) {
    return value.map((item) => scrubEventProperties(item, depth - 1)) as unknown as T
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        isSensitiveKey(key) ? REDACTED : scrubEventProperties(val, depth - 1),
      ]),
    ) as T
  }

  return value
}
